import React, { useState, useEffect, useMemo } from 'react';
import PainelTrocas from './dispositivos/PainelTrocas';
import ConciliacaoTrocas from './dispositivos/ConciliacaoTrocas';
import ControleDispositivos from './ControleDispositivos';
import { db, getCollectionName } from './firebase';
import {
  collection, query, onSnapshot, orderBy, doc,
  addDoc, updateDoc, deleteDoc, serverTimestamp,
} from './firestore-compat';

// ── Constantes ────────────────────────────────────────────────────────────────
const OPERADORAS = ['AMIL','BRADESCO','PEND. AMIL','UNIMED baby','Unimed senior','Klini','Unimed Brasil','Unimed Nova Iguaçu','Unimed Leste','Unimed Outros'];
const PRESTADORES  = ['Prontobaby','Externa'];
const SOLICITANTES = ['Núcleo','Outro'];
const PERIODICIDADE_OPTS = ['Mensal','2 meses','3 meses','4 meses','5 meses','6 meses'];
const UNIMED_OUTROS_OPTS = ['Unimed Leste','Unimed Nova Iguaçu','Unimed Outros','Acompanhar com a mãe'];

const STATUS_OPTS = [
  'Prévia','Em análise','Inserir no portal','Aguardando pedido','Pendente',
  'Aut. retroativa','Autorizado','Realizado','Negado','Cancelado',
];
const STATUS_COLOR = {
  'Prévia':            { bg:'#ede9fe', text:'#7c3aed' },
  'Autorizado':        { bg:'#dcfce7', text:'#15803d' },
  'Realizado':         { bg:'#d1fae5', text:'#065f46' },
  'Em análise':        { bg:'#fef9c3', text:'#a16207' },
  'Aguardando pedido': { bg:'#fef9c3', text:'#a16207' },
  'Pendente':          { bg:'#fef9c3', text:'#a16207' },
  'Inserir no portal': { bg:'#fef9c3', text:'#a16207' },
  'Negado':            { bg:'#fee2e2', text:'#b91c1c' },
  'Cancelado':         { bg:'#fee2e2', text:'#b91c1c' },
  'Aut. retroativa':   { bg:'#dbeafe', text:'#1d4ed8' },
};
const ANDAMENTO_OPTS = ['Não iniciado','Em andamento','Concluído'];
const ANDAMENTO_COLOR = { 'Não iniciado': '#64748b', 'Em andamento': '#a16207', 'Concluído': '#15803d' };
const STATUS_CANULA = {
  'Em dia':   { bg:'#dcfce7', text:'#15803d' },
  'Próximo':  { bg:'#fef9c3', text:'#a16207' },
  'Atrasado': { bg:'#fee2e2', text:'#b91c1c' },
  'Sem data': { bg:'#f1f5f9', text:'#64748b' },
};

const PIPELINE_COLUNAS = [
  { key:'previa',     label:'Prévia',         color:'#7c3aed', statuses:['Prévia'] },
  { key:'solicitado', label:'Solicitado',     color:'#d97706', statuses:['Em análise'] },
  { key:'cotacao',    label:'Em Cotação',     color:'#2563eb', statuses:['Inserir no portal','Aguardando pedido'] },
  { key:'aguardando', label:'Ag. Aprovação',  color:'#ea580c', statuses:['Pendente','Aut. retroativa'] },
  { key:'agendado',   label:'Autorizado',     color:'#16a34a', statuses:['Autorizado'] },
  { key:'realizado',  label:'Realizado',      color:'#0d9488', statuses:['Realizado'] },
];
const PIPELINE_TERMINAL = ['Negado','Cancelado'];

const OP_COLOR = {
  'AMIL':          { bg:'#ede9fe', text:'#6d28d9' },
  'BRADESCO':      { bg:'#fee2e2', text:'#b91c1c' },
  'PEND. AMIL':    { bg:'#fde68a', text:'#b45309' },
  'UNIMED baby':   { bg:'#d1fae5', text:'#065f46' },
  'Unimed senior': { bg:'#7c3aed', text:'#fff'    },
  'Klini':         { bg:'#fce7f3', text:'#9d174d' },
  'Unimed Brasil': { bg:'#d1fae5', text:'#065f46' },
};

const P = {
  bg:'#f8fafc', white:'#fff', border:'#e2e8f0', text:'#1e293b',
  muted:'#64748b', primary:'#6366f1', green:'#22c55e',
  red:'#ef4444', amber:'#f59e0b', blue:'#3b82f6', gray:'#94a3b8',
};

const CANULA_CONFIGS = {
  unimed_brasil: {
    cols:   ['Paciente','Médica','Cânula','Referência','Local'],
    fields: [
      { key:'paciente',      label:'Paciente',           type:'text',   bold:true, required:true },
      { key:'medica',        label:'Médica responsável', type:'text',   placeholder:'Ex.: Dra. Bárbara' },
      { key:'canula',        label:'Modelo da Cânula',   type:'text',   placeholder:'Ex.: Shiley 5,5 com cuff' },
      { key:'referencia',    label:'Referência',         type:'text' },
      { key:'local',         label:'Local da troca',     type:'text',   placeholder:'CPL, PB, Hospitalar…' },
      { key:'ultima_troca',  label:'Última troca',       type:'date' },
      { key:'periodicidade', label:'Periodicidade',      type:'select', options:PERIODICIDADE_OPTS },
    ],
  },
  // Vieram da tela de Remoções (abas "Unimed Outros" e "Bradesco") — cânula é dispositivo
  unimed_outros: {
    cols:   ['Paciente','Médica','Regional','Cânula'],
    fields: [
      { key:'paciente',      label:'Paciente',           type:'text',   bold:true, required:true },
      { key:'medica',        label:'Médica responsável', type:'text' },
      { key:'regional',      label:'Regional Unimed',    type:'select', options:UNIMED_OUTROS_OPTS },
      { key:'canula',        label:'Modelo da Cânula',   type:'text',   placeholder:'Ex.: Shiley 5,5 com cuff' },
      { key:'ultima_troca',  label:'Última troca',       type:'date' },
      { key:'periodicidade', label:'Periodicidade',      type:'select', options:PERIODICIDADE_OPTS },
    ],
  },
  bradesco: {
    banner: 'BRADESCO: a mãe é quem agenda — exceto a paciente Angel, que faz a troca de cânula no CPL.',
    cols:   ['Paciente','Cânula'],
    fields: [
      { key:'paciente',      label:'Paciente',           type:'text', bold:true, required:true },
      { key:'canula',        label:'Modelo da Cânula',   type:'text', placeholder:'Ex.: Shiley 4,5 C/CUFF' },
      { key:'ultima_troca',  label:'Última troca',       type:'date' },
    ],
  },
  sondas: {
    cols:   ['Paciente','Plano','Médica','Modelo'],
    fields: [
      { key:'paciente',      label:'Paciente',           type:'text',   bold:true, required:true },
      { key:'plano',         label:'Plano',              type:'select', options:OPERADORAS },
      { key:'medica',        label:'Médica responsável', type:'text' },
      { key:'modelo',        label:'Modelo da Sonda',    type:'text',   placeholder:'Ex.: Jejunal transpilórica 22FR' },
      { key:'ultima_troca',  label:'Última troca',       type:'date' },
      { key:'periodicidade', label:'Periodicidade',      type:'select', options:PERIODICIDADE_OPTS },
    ],
  },
};

// Tela "Dispositivos": tudo de dispositivo num lugar só (antes: "Troca de Dispositivo",
// "Controle dos Dispositivos" e as abas de cânulas da tela de Remoções).
const TABS = [
  { k:'painel',     l:'📋 Painel de trocas' },
  { k:'conciliacao', l:'🔎 Conciliação de trocas' },
  { k:'uso',        l:'Pacientes com dispositivo' },
  { k:'agendadas',  l:'🏥 Trocas hospitalares' },
  { k:'concluidos', l:'✅ Hospitalares concluídas' },
  { k:'canulas',    l:'🏠 Cânulas e sondas em casa' },
  { k:'pipeline',   l:'📊 Acompanhamento' },
];
const LISTAS_CANULAS = [
  { tipo:'unimed_brasil', l:'Cânula — Unimed Brasil' },
  { tipo:'unimed_outros', l:'Cânula — Unimed Outros' },
  { tipo:'bradesco',      l:'Cânula — Bradesco' },
  { tipo:'sondas',        l:'Sondas Gastro/Jejuno HB' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(s) {
  if (!s || typeof s !== 'string') return '—';
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '—';
  return `${match[3]}/${match[2]}/${match[1]}`;
}
function toSortDate(val) {
  if (!val || typeof val !== 'string') return '';
  const m = val.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}
function todayISO() { return new Date().toISOString().split('T')[0]; }
function periodicidadeEmMeses(p) {
  return { 'Mensal':1,'2 meses':2,'3 meses':3,'4 meses':4,'5 meses':5,'6 meses':6 }[p] || 1;
}
function calcProxima(ultima, periodo) {
  if (!ultima || !periodo) return '';
  const d = new Date(ultima + 'T00:00');
  if (isNaN(d)) return '';
  d.setMonth(d.getMonth() + periodicidadeEmMeses(periodo));
  return d.toISOString().split('T')[0];
}
function calcStatusCanula(proxima) {
  if (!proxima) return 'Sem data';
  const diff = Math.ceil((new Date(proxima + 'T00:00') - new Date()) / 86400000);
  if (diff < 0) return 'Atrasado';
  if (diff <= 7) return 'Próximo';
  return 'Em dia';
}

// ── Mini-componentes ──────────────────────────────────────────────────────────
function StatusBadge({ status, map = STATUS_COLOR, small }) {
  const c = map[status] || { bg:'#f1f5f9', text:'#64748b' };
  return (
    <span style={{ background:c.bg, color:c.text, padding:small?'2px 7px':'3px 10px',
      borderRadius:20, fontWeight:700, fontSize:small?'0.68rem':'0.74rem', whiteSpace:'nowrap' }}>
      {status || '—'}
    </span>
  );
}
function OpBadge({ op }) {
  const c = OP_COLOR[op] || { bg:'#e2e8f0', text:'#1e293b' };
  return (
    <span style={{ background:c.bg, color:c.text, padding:'2px 9px', borderRadius:20,
      fontWeight:700, fontSize:'0.72rem', whiteSpace:'nowrap' }}>{op || '—'}</span>
  );
}
function KpiCard({ label, value, color, onClick, sub }) {
  return (
    <div onClick={onClick} style={{ background:P.white, border:`1px solid ${P.border}`, borderRadius:10,
      padding:'12px 18px', minWidth:130, flex:1, cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ fontSize:'0.68rem', fontWeight:700, color:P.muted, textTransform:'uppercase', letterSpacing:.5 }}>{label}</div>
      <div style={{ fontSize:'1.8rem', fontWeight:800, color, lineHeight:1.1, margin:'4px 0 0' }}>{value}</div>
      {sub && <div style={{ fontSize:'0.68rem', color:P.muted, marginTop:2 }}>{sub}</div>}
    </div>
  );
}
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:P.white, borderRadius:14, width:'100%', maxWidth:wide?800:560,
        maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.25)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'16px 22px', borderBottom:`1px solid ${P.border}`,
          position:'sticky', top:0, background:P.white, zIndex:1 }}>
          <h2 style={{ margin:0, fontSize:'1rem', fontWeight:800, color:P.text }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'1.3rem', cursor:'pointer', color:P.muted }}>✕</button>
        </div>
        <div style={{ padding:'20px 22px' }}>{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:'block', fontSize:'0.71rem', fontWeight:700, color:P.muted,
        textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>
        {label}{required && <span style={{ color:P.red }}> *</span>}
      </label>
      {children}
    </div>
  );
}
const inp = {
  width:'100%', padding:'8px 11px', border:`1px solid ${P.border}`, borderRadius:8,
  fontSize:'0.85rem', background:P.white, color:P.text, boxSizing:'border-box', outline:'none',
};
function Sel({ value, onChange, options, placeholder }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={inp}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
function Btn({ children, onClick, disabled, color = P.primary, small }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ background:disabled?P.border:color, color:'#fff', border:'none',
        borderRadius:8, padding:small?'5px 12px':'8px 18px',
        fontWeight:700, fontSize:small?'0.78rem':'0.84rem',
        cursor:disabled?'default':'pointer', opacity:disabled?.6:1 }}>
      {children}
    </button>
  );
}
function GhostBtn({ children, onClick, small }) {
  return (
    <button onClick={onClick}
      style={{ background:'none', border:`1px solid ${P.border}`, color:P.muted,
        borderRadius:8, padding:small?'4px 10px':'7px 14px',
        fontWeight:600, fontSize:small?'0.78rem':'0.82rem', cursor:'pointer' }}>
      {children}
    </button>
  );
}
function TH({ children }) {
  return <th style={{ padding:'9px 12px', textAlign:'left', fontSize:'0.68rem', fontWeight:700,
    color:P.muted, textTransform:'uppercase', letterSpacing:.5, borderBottom:`1px solid ${P.border}`,
    whiteSpace:'nowrap' }}>{children}</th>;
}

// ── Pipeline Kanban ────────────────────────────────────────────────────────────
function PipelineCard({ r, onChangeStatus }) {
  const [menu, setMenu] = useState(false);
  const sc = STATUS_COLOR[r.status] || { bg:'#f1f5f9', text:'#64748b' };
  return (
    <div style={{ background:'#fff', borderRadius:10, border:'1px solid #e2e8f0', padding:'10px 12px', marginBottom:8,
      boxShadow:'0 1px 3px rgba(0,0,0,0.05)', borderLeft:`3px solid ${sc.text}` }}>
      <div style={{ fontWeight:700, color:'#1e293b', fontSize:'0.85rem', marginBottom:4 }}>{r.paciente}</div>
      {r.operadora && <OpBadge op={r.operadora} />}
      {r.dispositivo && <span style={{ marginLeft:4, fontSize:'0.7rem', color:'#64748b' }}>{r.dispositivo}</span>}
      {(r.data||r.horario) && (
        <div style={{ color:'#64748b', fontSize:'0.75rem', marginTop:4 }}>{fmtDate(r.data)}{r.horario?` · ${r.horario}`:''}</div>
      )}
      <div style={{ marginTop:8, position:'relative' }}>
        <button onClick={()=>setMenu(v=>!v)} style={{ width:'100%', textAlign:'left', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:'0.72rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <StatusBadge status={r.status} small />
          <span style={{ color:'#94a3b8' }}>▾</span>
        </button>
        {menu && (
          <div style={{ position:'absolute', bottom:'calc(100% + 4px)', left:0, right:0, zIndex:200, background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', maxHeight:220, overflowY:'auto' }}>
            {STATUS_OPTS.filter(s=>s!==r.status).map(s=>(
              <div key={s} onClick={()=>{ onChangeStatus(r,s); setMenu(false); }}
                style={{ padding:'7px 12px', cursor:'pointer', fontSize:'0.8rem' }}
                onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                onMouseLeave={e=>e.currentTarget.style.background=''}>
                <StatusBadge status={s} small />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TabPipeline({ data, col }) {
  const [search, setSearch] = useState('');
  const [fOp,    setFOp]    = useState('');

  const filtered = useMemo(() => {
    let r = data;
    if (search) r = r.filter(x => x.paciente?.toLowerCase().includes(search.toLowerCase()));
    if (fOp)    r = r.filter(x => x.operadora === fOp);
    return r;
  }, [data, search, fOp]);

  const byColuna = useMemo(() => {
    const map = {};
    PIPELINE_COLUNAS.forEach(c => { map[c.key] = []; });
    filtered.forEach(r => {
      const found = PIPELINE_COLUNAS.find(c => c.statuses.includes(r.status));
      if (found) map[found.key].push(r);
    });
    return map;
  }, [filtered]);

  const terminal = useMemo(() => filtered.filter(r => PIPELINE_TERMINAL.includes(r.status)), [filtered]);

  const kpis = useMemo(() => ({
    total:     data.length,
    previa:    data.filter(r=>r.status==='Prévia').length,
    agendado:  data.filter(r=>r.status==='Autorizado').length,
    realizado: data.filter(r=>r.status==='Realizado').length,
    cancelado: data.filter(r=>PIPELINE_TERMINAL.includes(r.status)).length,
  }), [data]);

  const changeStatus = async (r, status) => {
    await updateDoc(doc(db, col, r.id), { status, updatedAt: serverTimestamp() });
  };

  return (
    <div>
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:20 }}>
        <KpiCard label="Total"           value={kpis.total}     color={P.muted} />
        <KpiCard label="Prévias"         value={kpis.previa}    color='#7c3aed' />
        <KpiCard label="Autorizadas"     value={kpis.agendado}  color={P.green} />
        <KpiCard label="Realizadas"      value={kpis.realizado} color='#0d9488' />
        <KpiCard label="Canceladas/Neg." value={kpis.cancelado} color={P.red}   />
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <input placeholder="Buscar paciente…" value={search} onChange={e=>setSearch(e.target.value)}
          style={{ border:`1px solid ${P.border}`, borderRadius:8, padding:'7px 12px', fontSize:'0.84rem', flex:1, minWidth:160, outline:'none' }} />
        <select value={fOp} onChange={e=>setFOp(e.target.value)}
          style={{ border:`1px solid ${P.border}`, borderRadius:8, padding:'7px 10px', fontSize:'0.84rem', color:fOp?P.text:P.muted }}>
          <option value="">Todas operadoras</option>
          {OPERADORAS.map(o=><option key={o} value={o}>{o}</option>)}
        </select>
        {(search||fOp) && (
          <button onClick={()=>{ setSearch(''); setFOp(''); }}
            style={{ background:'none', border:`1px solid ${P.border}`, borderRadius:8, padding:'7px 12px', cursor:'pointer', fontSize:'0.82rem', color:P.muted }}>
            Limpar
          </button>
        )}
      </div>

      <div style={{ display:'flex', gap:12, overflowX:'auto', paddingBottom:16 }}>
        {PIPELINE_COLUNAS.map(coluna => (
          <div key={coluna.key} style={{ minWidth:220, flex:'0 0 220px' }}>
            <div style={{ background:coluna.color, color:'#fff', fontWeight:700, fontSize:'0.78rem', padding:'8px 12px', borderRadius:'8px 8px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span>{coluna.label}</span>
              <span style={{ background:'rgba(255,255,255,0.25)', borderRadius:10, padding:'1px 8px', fontWeight:800 }}>{byColuna[coluna.key]?.length||0}</span>
            </div>
            <div style={{ background:'#f8fafc', borderRadius:'0 0 8px 8px', border:'1px solid #e2e8f0', borderTop:'none', padding:'10px 8px', minHeight:100 }}>
              {(byColuna[coluna.key]||[]).length === 0
                ? <div style={{ color:'#cbd5e1', fontSize:'0.75rem', textAlign:'center', padding:'24px 0' }}>Vazio</div>
                : (byColuna[coluna.key]||[]).map(r => <PipelineCard key={r.id} r={r} onChangeStatus={changeStatus} />)
              }
            </div>
          </div>
        ))}
      </div>

      {terminal.length > 0 && (
        <div style={{ marginTop:20 }}>
          <div style={{ fontSize:'0.78rem', fontWeight:700, color:P.muted, textTransform:'uppercase', letterSpacing:.5, marginBottom:10 }}>
            Cancelados / Negados ({terminal.length})
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {terminal.map(r=>(
              <div key={r.id} style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'8px 14px', fontSize:'0.8rem' }}>
                <strong style={{ color:P.text }}>{r.paciente}</strong>
                <span style={{ marginLeft:8 }}><StatusBadge status={r.status} small /></span>
                {r.data&&<span style={{ marginLeft:8, color:P.muted }}>{fmtDate(r.data)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab: Trocas Agendadas ────────────────────────────────────────────────────────
const EMPTY_TROCA = {
  paciente:'', idade:'', dispositivo:'', motivo:'', operadora:'', data:'', horario:'',
  solicitante:'Núcleo', prestador:'Prontobaby', status:'Em análise',
  local:'', protocolo:'', observacoes:'',
  andamento:'Não iniciado', aditivo_iw:'', input_impressos:'',
};

function TabAgendadas({ data, col, onlyConcluidos = false }) {
  const [subTab,   setSubTab]   = useState('agendadas');
  const [search,   setSearch]   = useState('');
  const [fStatus,  setFStatus]  = useState('');
  const [fOp,      setFOp]      = useState('');
  const [fDIni,    setFDIni]    = useState('');
  const [fDFim,    setFDFim]    = useState('');
  const [modal,    setModal]    = useState(false);
  const [edit,     setEdit]     = useState(null);
  const [form,     setForm]     = useState(EMPTY_TROCA);
  const [saving,   setSaving]   = useState(false);
  const [delId,    setDelId]    = useState(null);
  const [inlineSt, setInlineSt] = useState(null);
  const [showHoje, setShowHoje] = useState(false);

  const openNew  = () => { setForm(EMPTY_TROCA); setEdit(null); setModal(true); };
  const openEdit = r  => { setForm({ ...EMPTY_TROCA, ...r }); setEdit(r); setModal(true); };
  const close    = () => { setModal(false); setEdit(null); };

  const save = async () => {
    if (!form.paciente.trim()) return;
    setSaving(true);
    try {
      if (edit) await updateDoc(doc(db, col, edit.id), { ...form, updatedAt: serverTimestamp() });
      else      await addDoc(collection(db, col), { ...form, criadoEm: serverTimestamp() });
      close();
    } catch(e) { console.error(e); alert('Erro ao salvar: ' + (e.message || JSON.stringify(e))); }
    setSaving(false);
  };

  const quickStatus = async (r, status) => {
    await updateDoc(doc(db, col, r.id), { status, updatedAt: serverTimestamp() });
    setInlineSt(null);
  };

  const rows = useMemo(() => {
    let r = data;
    if (search)  r = r.filter(x => ((x.paciente||'')+(x.dispositivo||'')+(x.motivo||'')+(x.observacoes||'')).toLowerCase().includes(search.toLowerCase()));
    if (fStatus) r = r.filter(x => x.status    === fStatus);
    if (fOp)     r = r.filter(x => x.operadora === fOp);
    if (fDIni)   r = r.filter(x => x.data >= fDIni);
    if (fDFim)   r = r.filter(x => x.data <= fDFim);
    return [...r].sort((a, b) => {
      const da = toSortDate(a.data); const db_ = toSortDate(b.data);
      if (!da && !db_) return 0; if (!da) return 1; if (!db_) return -1;
      return da < db_ ? -1 : da > db_ ? 1 : 0;
    });
  }, [data, search, fStatus, fOp, fDIni, fDFim]);

  const rowsAgendadas  = useMemo(() => rows.filter(r => r.status !== 'Realizado'), [rows]);
  const rowsConcluidas = useMemo(() => rows.filter(r => r.status === 'Realizado').sort((a, b) => {
    const da = toSortDate(a.data); const db_ = toSortDate(b.data);
    if (!da && !db_) return 0; if (!da) return 1; if (!db_) return -1;
    return da > db_ ? -1 : da < db_ ? 1 : 0;
  }), [rows]);
  const rowsConcluidosAndamento = useMemo(() => rows.filter(r => r.andamento === 'Concluído').sort((a, b) => {
    const da = toSortDate(a.data); const db_ = toSortDate(b.data);
    if (!da && !db_) return 0; if (!da) return 1; if (!db_) return -1;
    return da > db_ ? -1 : da < db_ ? 1 : 0;
  }), [rows]);
  const displayRows = onlyConcluidos ? rowsConcluidosAndamento : (subTab === 'concluidas' ? rowsConcluidas : rowsAgendadas);

  const hoje = todayISO();
  const kpis = useMemo(() => ({
    total:     rowsAgendadas.length,
    autorizado:rowsAgendadas.filter(x => x.status === 'Autorizado').length,
    pendente:  rowsAgendadas.filter(x => ['Em análise','Pendente','Aguardando pedido','Inserir no portal'].includes(x.status)).length,
    cancelado: rowsAgendadas.filter(x => ['Cancelado','Negado'].includes(x.status)).length,
    hoje:      data.filter(x => x.data === hoje && x.status !== 'Realizado'),
  }), [rowsAgendadas, data, hoje]);

  const hasF = search||fStatus||fOp||fDIni||fDFim;
  const clrF = () => { setSearch(''); setFStatus(''); setFOp(''); setFDIni(''); setFDFim(''); };

  return (
    <div>
      {onlyConcluidos ? (
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
          <KpiCard label="✅ Concluídos" value={rowsConcluidosAndamento.length} color="#15803d" />
        </div>
      ) : (
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
          <KpiCard label="Total"       value={kpis.total}       color={P.muted} />
          <KpiCard label="Autorizadas" value={kpis.autorizado}  color={P.green} />
          <KpiCard label="Pendentes"   value={kpis.pendente}    color={P.amber} />
          <KpiCard label="Canceladas"  value={kpis.cancelado}   color={P.red}   />
          <KpiCard label="🔧 Hoje"    value={kpis.hoje.length} color='#0d9488'
            sub={kpis.hoje.length ? 'ver lista →' : 'nenhuma hoje'}
            onClick={kpis.hoje.length ? ()=>setShowHoje(true) : undefined} />
        </div>
      )}

      {!onlyConcluidos && (
      <div style={{ display:'flex', gap:0, marginBottom:14, background:'#f1f5f9', borderRadius:10, padding:3, width:'fit-content' }}>
        {[
          { k:'agendadas',  l:'⚡ Agendadas',  count: rowsAgendadas.length,  color:'#6366f1' },
          { k:'concluidas', l:'✅ Concluídas', count: rowsConcluidas.length, color:'#0d9488' },
        ].map(({ k, l, count, color }) => (
          <button key={k} onClick={() => setSubTab(k)} style={{
            border: 'none', borderRadius: 8, padding: '6px 18px', cursor: 'pointer',
            fontSize: '0.82rem', fontWeight: 700, transition: 'all 0.15s',
            background: subTab === k ? P.white : 'transparent',
            color: subTab === k ? color : '#94a3b8',
            boxShadow: subTab === k ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
          }}>
            {l}
            <span style={{ marginLeft: 6, background: subTab === k ? color : '#e2e8f0', color: subTab === k ? '#fff' : '#94a3b8',
              borderRadius: 20, padding: '1px 7px', fontSize: '0.7rem', fontWeight: 700 }}>
              {count}
            </span>
          </button>
        ))}
      </div>
      )}

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
        {!onlyConcluidos && subTab === 'agendadas' && <Btn onClick={openNew}>+ Nova Troca</Btn>}
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar paciente, dispositivo ou motivo…"
          style={{ ...inp, maxWidth:260, flex:1 }} />
        <select value={fStatus} onChange={e=>setFStatus(e.target.value)} style={{ ...inp, maxWidth:170 }}>
          <option value="">Todos os status</option>
          {STATUS_OPTS.filter(s => onlyConcluidos ? true : (subTab === 'concluidas' ? s === 'Realizado' : s !== 'Realizado')).map(s=><option key={s}>{s}</option>)}
        </select>
        <select value={fOp} onChange={e=>setFOp(e.target.value)} style={{ ...inp, maxWidth:160 }}>
          <option value="">Todas operadoras</option>
          {OPERADORAS.map(o=><option key={o}>{o}</option>)}
        </select>
        <input type="date" value={fDIni} onChange={e=>setFDIni(e.target.value)} style={{ ...inp, maxWidth:148 }} />
        <input type="date" value={fDFim} onChange={e=>setFDFim(e.target.value)} style={{ ...inp, maxWidth:148 }} />
        {hasF && <GhostBtn onClick={clrF}>✕ Limpar</GhostBtn>}
        <span style={{ marginLeft:'auto', fontSize:'0.75rem', color:P.muted }}>{displayRows.length} registro{displayRows.length!==1?'s':''}</span>
      </div>

      <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}` }}>
        <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.75rem', tableLayout:'fixed' }}>
          <colgroup>
            <col style={{ width:'12%' }} />{/* Paciente */}
            <col style={{ width:'12%' }} />{/* Dispositivo */}
            <col style={{ width:'11%' }} />{/* Motivo */}
            <col style={{ width:'9%'  }} />{/* Operadora */}
            <col style={{ width:'8%'  }} />{/* Data/Hora */}
            <col style={{ width:'8%'  }} />{/* Solic./Prest. */}
            <col style={{ width:'8%'  }} />{/* Status */}
            <col style={{ width:'7%'  }} />{/* Andamento */}
            <col style={{ width:'7%'  }} />{/* Aditivo IW */}
            <col style={{ width:'7%'  }} />{/* Input Impressos */}
            <col style={{ width:'6%'  }} />{/* Local */}
            <col style={{ width:'5%'  }} />{/* Ações */}
          </colgroup>
          <thead><tr>
            <TH>Paciente</TH><TH>Dispositivo</TH><TH>Motivo</TH><TH>Operadora</TH><TH>Data / Hora</TH>
            <TH>Solic. / Prest.</TH><TH>Status</TH><TH>Andamento</TH>
            <TH>Aditivo no IW</TH><TH>Input em Impressos</TH>
            <TH>Local</TH><TH>Ações</TH>
          </tr></thead>
          <tbody>
            {displayRows.length === 0 && <tr><td colSpan={12} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhum registro.</td></tr>}
            {displayRows.map((r,i) => (
              <tr key={r.id} style={{ borderBottom:`1px solid #f1f5f9`, background:i%2===0?'transparent':'#fafafa' }}>
                <td style={{ padding:'6px 8px', fontWeight:600, color:P.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.paciente}>{r.paciente}</td>
                <td style={{ padding:'6px 8px', color:P.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.dispositivo}>{r.dispositivo||'—'}</td>
                <td style={{ padding:'6px 8px', color:P.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.motivo}>{r.motivo||'—'}</td>
                <td style={{ padding:'6px 8px' }}><OpBadge op={r.operadora} /></td>
                <td style={{ padding:'6px 8px', color:P.text, whiteSpace:'nowrap' }}>
                  <div>{fmtDate(r.data)||'—'}</div>
                  <div style={{ color:P.muted, fontSize:'0.7rem' }}>{r.horario||'—'}</div>
                </td>
                <td style={{ padding:'6px 8px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  <div style={{ color:P.text }}>{r.solicitante||'—'}</div>
                  <div style={{ color:P.muted, fontSize:'0.7rem' }}>{r.prestador||'—'}</div>
                </td>
                <td style={{ padding:'6px 8px', position:'relative' }}>
                  <div onClick={()=>setInlineSt(inlineSt===r.id?null:r.id)} style={{ cursor:'pointer' }}>
                    <StatusBadge status={r.status} />
                  </div>
                  {inlineSt === r.id && (
                    <div style={{ position:'absolute', top:'100%', left:0, zIndex:100, background:P.white,
                      border:`1px solid ${P.border}`, borderRadius:10, padding:8,
                      boxShadow:'0 8px 24px rgba(0,0,0,.12)', minWidth:200 }}>
                      {STATUS_OPTS.map(s => (
                        <div key={s} onClick={()=>quickStatus(r,s)}
                          style={{ padding:'6px 10px', cursor:'pointer', borderRadius:7 }}
                          onMouseEnter={e=>e.currentTarget.style.background='#f1f5f9'}
                          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <StatusBadge status={s} small />
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td style={{ padding:'4px 6px' }}>
                  <select
                    value={r.andamento || 'Não iniciado'}
                    onChange={async e => { await updateDoc(doc(db, col, r.id), { andamento: e.target.value, updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6,
                      padding:'3px 4px', color: ANDAMENTO_COLOR[r.andamento || 'Não iniciado'],
                      fontWeight:700, background:P.white, cursor:'pointer' }}>
                    {ANDAMENTO_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </td>
                <td style={{ padding:'4px 6px' }}>
                  <select
                    value={r.aditivo_iw || ''}
                    onChange={async e => { await updateDoc(doc(db, col, r.id), { aditivo_iw: e.target.value, updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6,
                      padding:'3px 4px', color: r.aditivo_iw==='Sim'?'#15803d':r.aditivo_iw==='Não'?'#b91c1c':P.muted,
                      fontWeight: r.aditivo_iw?700:400, background:P.white, cursor:'pointer' }}>
                    <option value="">—</option>
                    <option value="Sim">Sim</option>
                    <option value="Não">Não</option>
                    <option value="N/A">N/A</option>
                  </select>
                </td>
                <td style={{ padding:'4px 6px' }}>
                  <select
                    value={r.input_impressos || ''}
                    onChange={async e => { await updateDoc(doc(db, col, r.id), { input_impressos: e.target.value, updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6,
                      padding:'3px 4px', color: r.input_impressos==='Sim'?'#15803d':r.input_impressos==='Não'?'#b91c1c':P.muted,
                      fontWeight: r.input_impressos?700:400, background:P.white, cursor:'pointer' }}>
                    <option value="">—</option>
                    <option value="Sim">Sim</option>
                    <option value="Não">Não</option>
                    <option value="N/A">N/A</option>
                  </select>
                </td>
                <td style={{ padding:'6px 8px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.local}>{r.local||'—'}</td>
                <td style={{ padding:'6px 8px', whiteSpace:'nowrap' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <GhostBtn small onClick={()=>openEdit(r)}>✏️</GhostBtn>
                    <GhostBtn small onClick={()=>setDelId(r.id)}>🗑</GhostBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showHoje && (
        <Modal title={`🔧 Trocas de Hoje — ${new Date().toLocaleDateString('pt-BR')}`} onClose={()=>setShowHoje(false)} wide>
          {kpis.hoje.length === 0 ? (
            <p style={{ color:P.muted, textAlign:'center', padding:'20px 0' }}>Nenhuma troca agendada para hoje.</p>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem' }}>
              <thead>
                <tr style={{ borderBottom:`2px solid ${P.border}` }}>
                  {['Horário','Paciente','Dispositivo','Operadora','Status'].map(h => (
                    <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:P.muted, fontWeight:700, fontSize:'0.69rem', textTransform:'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...kpis.hoje].sort((a,b)=>(a.horario||'').localeCompare(b.horario||'')).map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid #f1f5f9`, background: i%2===0?'transparent':'#fafafa' }}>
                    <td style={{ padding:'7px 10px', fontWeight:700, color:'#0d9488' }}>{r.horario||'—'}</td>
                    <td style={{ padding:'7px 10px', fontWeight:600, color:P.text }}>{r.paciente}{r.idade?` (${r.idade}a)`:''}</td>
                    <td style={{ padding:'7px 10px', color:P.text }}>{r.dispositivo||'—'}</td>
                    <td style={{ padding:'7px 10px' }}><OpBadge op={r.operadora} /></td>
                    <td style={{ padding:'7px 10px' }}><StatusBadge status={r.status} small /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}

      {modal && (
        <Modal title={edit?'Editar Troca':'Nova Troca de Dispositivo'} onClose={close} wide>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 18px' }}>
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Paciente" required>
                <input value={form.paciente} onChange={e=>setForm(f=>({...f,paciente:e.target.value}))} placeholder="Nome completo do paciente" style={inp} />
              </Field>
            </div>
            <Field label="Idade (anos)">
              <input type="number" min="0" max="130" value={form.idade||''} onChange={e=>setForm(f=>({...f,idade:e.target.value}))} placeholder="Ex.: 65" style={inp} />
            </Field>
            <Field label="Dispositivo" required>
              <input value={form.dispositivo} onChange={e=>setForm(f=>({...f,dispositivo:e.target.value}))} placeholder="Ex.: GTT, SNE, PICC, Cateter Vesical, Botton…" style={inp} />
            </Field>
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Motivo">
                <input value={form.motivo} onChange={e=>setForm(f=>({...f,motivo:e.target.value}))} placeholder="Ex.: Troca de rotina, obstrução, saída acidental…" style={inp} />
              </Field>
            </div>
            <Field label="Operadora" required><Sel value={form.operadora} onChange={v=>setForm(f=>({...f,operadora:v}))} options={OPERADORAS} placeholder="Selecionar…" /></Field>
            <Field label="Data da troca"><input type="date" value={form.data} onChange={e=>setForm(f=>({...f,data:e.target.value}))} style={inp} /></Field>
            <Field label="Horário"><input type="time" value={form.horario} onChange={e=>setForm(f=>({...f,horario:e.target.value}))} style={inp} /></Field>
            <Field label="Solicitante"><Sel value={form.solicitante} onChange={v=>setForm(f=>({...f,solicitante:v}))} options={SOLICITANTES} /></Field>
            <Field label="Prestador"><Sel value={form.prestador} onChange={v=>setForm(f=>({...f,prestador:v}))} options={PRESTADORES} /></Field>
            <Field label="Status"><Sel value={form.status} onChange={v=>setForm(f=>({...f,status:v}))} options={STATUS_OPTS} /></Field>
            <Field label="Local da troca">
              <input value={form.local} onChange={e=>setForm(f=>({...f,local:e.target.value}))} placeholder="Ex.: Domicílio, CPL, Hospitalar…" style={inp} />
            </Field>
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Protocolo / Nº autorização">
                <input value={form.protocolo} onChange={e=>setForm(f=>({...f,protocolo:e.target.value}))} placeholder="Nº do protocolo ou autorização da operadora" style={inp} />
              </Field>
            </div>
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Observações">
                <textarea value={form.observacoes} onChange={e=>setForm(f=>({...f,observacoes:e.target.value}))} rows={3} placeholder="Modelo do dispositivo, telefone de contato, nº do pedido…" style={{ ...inp, resize:'vertical' }} />
              </Field>
            </div>
          </div>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <Btn onClick={save} disabled={saving||!form.paciente.trim()}>{saving?'Salvando…':'Salvar troca'}</Btn>
            <GhostBtn onClick={close}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}

      {delId && (
        <Modal title="Confirmar exclusão" onClose={()=>setDelId(null)}>
          <p style={{ color:P.text, marginBottom:20 }}>Deseja excluir este registro? Esta ação não pode ser desfeita.</p>
          <div style={{ display:'flex', gap:10 }}>
            <Btn color={P.red} onClick={()=>{ deleteDoc(doc(db,col,delId)); setDelId(null); }}>Excluir</Btn>
            <GhostBtn onClick={()=>setDelId(null)}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab: Cânulas / Sondas (migrado de Gestão de Remoções — mesma coleção rm_canulas) ─
function TabCanulas({ data, tipo, config, col }) {
  const [search, setSearch]   = useState('');
  const [fStatus,setFStatus]  = useState('');
  const [form,   setForm]     = useState({});
  const [edit,   setEdit]     = useState(null);
  const [modal,  setModal]    = useState(false);
  const [saving, setSaving]   = useState(false);

  const emptyForm = useMemo(() => config.fields.reduce((a,f) => ({ ...a, [f.key]:'' }), { tipo }), [tipo, config]);
  const openNew  = () => { setForm(emptyForm); setEdit(null); setModal(true); };
  const openEdit = r  => { setForm({ ...emptyForm, ...r }); setEdit(r); setModal(true); };
  const close    = () => { setModal(false); setEdit(null); };

  const save = async () => {
    if (!form.paciente?.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.ultima_troca && payload.periodicidade)
        payload.proxima_troca = calcProxima(payload.ultima_troca, payload.periodicidade);
      payload.status_calculado = calcStatusCanula(payload.proxima_troca);
      if (edit) await updateDoc(doc(db, col, edit.id), { ...payload, updatedAt: serverTimestamp() });
      else      await addDoc(collection(db, col), { ...payload, tipo, criadoEm: serverTimestamp() });
      close();
    } catch(e) { console.error(e); alert('Erro ao salvar: ' + (e.message || JSON.stringify(e))); }
    setSaving(false);
  };

  const registrarTroca = async r => {
    const hoje = todayISO();
    const prox = r.periodicidade ? calcProxima(hoje, r.periodicidade) : '';
    await updateDoc(doc(db, col, r.id), {
      ultima_troca: hoje, proxima_troca: prox,
      status_calculado: calcStatusCanula(prox),
      updatedAt: serverTimestamp(),
    });
  };

  const rows = useMemo(() => {
    let r = data.filter(x => x.tipo === tipo);
    if (search)  r = r.filter(x => x.paciente?.toLowerCase().includes(search.toLowerCase()));
    if (fStatus) r = r.filter(x => (x.status_calculado || calcStatusCanula(x.proxima_troca)) === fStatus);
    return [...r].sort((a,b) => (a.proxima_troca||'9').localeCompare(b.proxima_troca||'9'));
  }, [data, tipo, search, fStatus]);

  const all  = data.filter(x => x.tipo === tipo);
  const kpis = useMemo(() => ({
    atrasado: all.filter(x => (x.status_calculado||calcStatusCanula(x.proxima_troca))==='Atrasado').length,
    proximo:  all.filter(x => (x.status_calculado||calcStatusCanula(x.proxima_troca))==='Próximo').length,
    emDia:    all.filter(x => (x.status_calculado||calcStatusCanula(x.proxima_troca))==='Em dia').length,
    total:    all.length,
  }), [all]);

  return (
    <div>
      {config.banner && (
        <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:10, padding:'10px 16px', marginBottom:18, fontSize:'0.84rem', color:'#92400e', fontWeight:600 }}>
          ⚠️ {config.banner}
        </div>
      )}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:18 }}>
        <KpiCard label="Atrasados" value={kpis.atrasado} color={P.red}   />
        <KpiCard label="Próximos"  value={kpis.proximo}  color={P.amber} />
        <KpiCard label="Em dia"    value={kpis.emDia}    color={P.green} />
        <KpiCard label="Total"     value={kpis.total}    color={P.muted} />
      </div>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14 }}>
        <Btn onClick={openNew}>+ Novo Paciente</Btn>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar paciente…" style={{ ...inp, maxWidth:240 }} />
        <select value={fStatus} onChange={e=>setFStatus(e.target.value)} style={{ ...inp, maxWidth:160 }}>
          <option value="">Todos os status</option>
          {Object.keys(STATUS_CANULA).map(s=><option key={s}>{s}</option>)}
        </select>
        {(search||fStatus) && <GhostBtn onClick={()=>{setSearch('');setFStatus('');}}>✕ Limpar</GhostBtn>}
      </div>
      <div style={{ overflowX:'auto', background:P.white, borderRadius:12, border:`1px solid ${P.border}` }}>
        <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.8rem' }}>
          <thead><tr>
            {config.cols.map(h => <TH key={h}>{h}</TH>)}
            <TH>Última Troca</TH><TH>Periodicidade</TH><TH>Próxima Troca</TH><TH>Troca Prevista</TH><TH>Status</TH><TH>Ações</TH>
          </tr></thead>
          <tbody>
            {rows.length===0 && <tr><td colSpan={config.cols.length+6} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhum registro.</td></tr>}
            {rows.map((r,i) => {
              const st = r.status_calculado || calcStatusCanula(r.proxima_troca);
              return (
                <tr key={r.id} style={{ borderBottom:`1px solid #f1f5f9`,
                  background:st==='Atrasado'?'#fff5f5':st==='Próximo'?'#fffbeb':i%2===0?'transparent':'#fafafa' }}>
                  {config.fields.filter(f=>config.cols.includes(f.label)).map(f=>(
                    <td key={f.key} style={{ padding:'9px 12px', color:f.bold?P.text:P.muted,
                      fontWeight:f.bold?600:400, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {r[f.key]||'—'}
                    </td>
                  ))}
                  <td style={{ padding:'9px 12px', color:P.muted, whiteSpace:'nowrap' }}>{fmtDate(r.ultima_troca)}</td>
                  <td style={{ padding:'9px 12px', color:P.muted, whiteSpace:'nowrap' }}>{r.periodicidade||'—'}</td>
                  <td style={{ padding:'9px 12px', whiteSpace:'nowrap', fontWeight:600,
                    color:st==='Atrasado'?P.red:st==='Próximo'?P.amber:P.green }}>{fmtDate(r.proxima_troca)}</td>
                  <td style={{ padding:'9px 12px', whiteSpace:'nowrap', fontWeight:600,
                    color: (() => { const calc=calcProxima(r.ultima_troca,r.periodicidade); const s=calcStatusCanula(calc); return s==='Atrasado'?P.red:s==='Próximo'?P.amber:P.green; })() }}>
                    {fmtDate(calcProxima(r.ultima_troca, r.periodicidade)) || '—'}
                  </td>
                  <td style={{ padding:'9px 12px' }}><StatusBadge status={st} map={STATUS_CANULA} /></td>
                  <td style={{ padding:'9px 12px', whiteSpace:'nowrap' }}>
                    <div style={{ display:'flex', gap:6 }}>
                      <Btn small color={P.green} onClick={()=>registrarTroca(r)}>✔ Troca</Btn>
                      <GhostBtn small onClick={()=>openEdit(r)}>✏️</GhostBtn>
                      <GhostBtn small onClick={()=>deleteDoc(doc(db,col,r.id))}>🗑</GhostBtn>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={edit?'Editar':'Novo Paciente'} onClose={close}>
          {config.fields.map(f => (
            <Field key={f.key} label={f.label} required={f.required}>
              {f.type==='select'
                ? <Sel value={form[f.key]||''} onChange={v=>setForm(x=>({...x,[f.key]:v}))} options={f.options||[]} placeholder="Selecionar…" />
                : f.type==='date'
                ? <input type="date" value={form[f.key]||''} onChange={e=>{
                    const nf={...form,[f.key]:e.target.value};
                    if (f.key==='ultima_troca'&&nf.periodicidade) nf.proxima_troca=calcProxima(e.target.value,nf.periodicidade);
                    setForm(nf);
                  }} style={inp} />
                : <input value={form[f.key]||''} onChange={e=>{
                    const nf={...form,[f.key]:e.target.value};
                    if (f.key==='periodicidade'&&nf.ultima_troca) nf.proxima_troca=calcProxima(nf.ultima_troca,e.target.value);
                    setForm(nf);
                  }} placeholder={f.placeholder||''} style={inp} />
              }
            </Field>
          ))}
          <Field label="Próxima troca (auto-calculada)">
            <input type="date" value={form.proxima_troca||''} onChange={e=>setForm(x=>({...x,proxima_troca:e.target.value}))} style={inp} />
          </Field>
          <Field label="Atualização / Observação">
            <textarea value={form.atualizacao||''} onChange={e=>setForm(x=>({...x,atualizacao:e.target.value}))} rows={2} style={{ ...inp, resize:'vertical' }} />
          </Field>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <Btn onClick={save} disabled={saving||!form.paciente?.trim()}>{saving?'Salvando…':'Salvar'}</Btn>
            <GhostBtn onClick={close}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function GestaoSolicitacaoTroca() {
  const [tab, setTab] = useState('painel');
  const [listaCanula, setListaCanula] = useState('unimed_brasil');
  const [trocas, setTrocas] = useState([]);
  const [canulas, setCanulas] = useState([]);
  const colTrocas  = getCollectionName('dispositivos_solicitacoes');
  const colCanulas = getCollectionName('rm_canulas');

  useEffect(() => {
    const q = query(collection(db, colTrocas), orderBy('criadoEm','desc'));
    return onSnapshot(q, snap => setTrocas(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [colTrocas]);

  useEffect(() => {
    const q = query(collection(db, colCanulas), orderBy('criadoEm','desc'));
    return onSnapshot(q, snap => setCanulas(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [colCanulas]);

  const tabStyle = k => ({
    padding:'8px 16px', borderRadius:'8px 8px 0 0',
    border:`1px solid ${P.border}`, borderBottom: k===tab ? `2px solid ${P.primary}` : `1px solid ${P.border}`,
    background: k===tab ? P.white : '#f1f5f9',
    color: k===tab ? P.primary : P.muted,
    fontWeight: k===tab ? 800 : 600, fontSize:'0.8rem', cursor:'pointer',
    position:'relative', top:'1px', whiteSpace:'nowrap',
  });

  return (
    <div style={{ background:P.bg, minHeight:'100vh', padding:'24px 24px 60px' }}>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ margin:0, fontSize:'1.4rem', fontWeight:800, color:P.text }}>🔧 Dispositivos</h1>
        <p style={{ margin:'4px 0 0', color:P.muted, fontSize:'0.83rem' }}>Quem usa, quando troca, onde troca (casa ou hospital) e em que etapa está — IW e Quality juntos</p>
      </div>

      <div style={{ display:'flex', flexWrap:'wrap', gap:4, borderBottom:`1px solid ${P.border}`, marginBottom:0 }}>
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={tabStyle(t.k)}>{t.l}</button>
        ))}
      </div>

      <div style={{ background:P.white, border:`1px solid ${P.border}`, borderTop:'none', borderRadius:'0 0 12px 12px', padding:'22px 20px' }}>
        {tab==='painel'     && <PainelTrocas solicitacoes={trocas} canulas={canulas} />}
        {tab==='conciliacao' && <ConciliacaoTrocas />}
        {tab==='uso'        && <ControleDispositivos embutido />}
        {tab==='pipeline'   && <TabPipeline   data={trocas} col={colTrocas} />}
        {tab==='agendadas'  && <TabAgendadas  data={trocas} col={colTrocas} />}
        {tab==='concluidos' && <TabAgendadas  data={trocas} col={colTrocas} onlyConcluidos />}
        {tab==='canulas' && (
          <>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:16 }}>
              {LISTAS_CANULAS.map(x => {
                const n = canulas.filter(c => c.tipo === x.tipo).length;
                const ativo = listaCanula === x.tipo;
                return (
                  <button key={x.tipo} onClick={() => setListaCanula(x.tipo)}
                    style={{ border:`1px solid ${ativo ? P.primary : P.border}`, background: ativo ? '#eef2ff' : P.white, color: ativo ? P.primary : P.muted,
                      borderRadius:999, padding:'6px 14px', fontSize:'0.8rem', fontWeight: ativo ? 800 : 600, cursor:'pointer' }}>
                    {x.l} · {n}
                  </button>
                );
              })}
            </div>
            <TabCanulas key={listaCanula} data={canulas} tipo={listaCanula} config={CANULA_CONFIGS[listaCanula]} col={colCanulas} />
          </>
        )}
      </div>
    </div>
  );
}
